// src/components/ManageUsers.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { createApiClient } from "../utils/apiClient";
import { useUser } from "../utils/userContext";
import { useTier } from "../utils/tierContext";
import { isAdmin as checkIsAdmin, isOwner as checkIsOwner, Roles, RoleLabels } from "../utils/access";
import type { StateInfo, UserInfo } from "../types/UserTypes";
import styles from "./ManageUsers.module.css";
import Toast from "./Toast";
import SearchableSelect from "./SearchableSelect";
import SaveEffectiveDateModal from "./SaveEffectiveDateModal";
import {
  EFFECTIVE_DATED_HISTORY_FEATURE,
  EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE,
  getEffectiveHistoryErrorMessage,
  todayYmd,
  withEffectiveStartDateIfAllowed,
} from "./EffectiveDateField";
import HistoricalReportingHistory, {
  HistoryIconButton,
  formatCurrency,
  type HistoryEndpointConfig,
} from "./HistoricalReportingHistory";

const emptyNewCompanyExpenseAccount = (states: any) => ({
  userId: 0,
  roleId: 4,
  businessName: "",
  firstName: "",
  lastName: "",
  streetAddress: "",
  city: "",
  state: states?.[0]?.stateId ?? "",
  zipCode: "",
  phoneNumber: "",
  emailAddress: "",
  taxId: "",
  dayRate: "",
  companyExpense: true,
  isActive: true,
});

function CopyIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M7.5 6.667V5.417A1.25 1.25 0 0 1 8.75 4.167h6.667a1.25 1.25 0 0 1 1.25 1.25v6.666a1.25 1.25 0 0 1-1.25 1.25h-1.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.583 7.5H11.25a1.25 1.25 0 0 1 1.25 1.25v6.667a1.25 1.25 0 0 1-1.25 1.25H4.583a1.25 1.25 0 0 1-1.25-1.25V8.75A1.25 1.25 0 0 1 4.583 7.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M16.667 10A6.667 6.667 0 1 1 14.714 5.286"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M16.667 3.333v4.167H12.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ManageUsers = () => {
  const authRequest = { ...loginRequest };
  const { token, getToken } = useAuthToken();
  const { user, activeTenantId } = useUser();
  const { hasFeature, getLimit, loading: tierLoading } = useTier();
  const canUseEffectiveHistory = hasFeature(EFFECTIVE_DATED_HISTORY_FEATURE);

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user?.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const [view, setView] = useState("users");
  const [usersFilter, setUsersFilter] = useState("active");

  const [updateStatus, setUpdateStatus] = useState<"success" | "error" | null>(null);
  const [updateMessage, setUpdateMessage] = useState("");
  const [taxIdVisible, setTaxIdVisible] = useState(false);
  const [revealedTaxId, setRevealedTaxId] = useState<string | null>(null);

  const toggleTaxIdVisibility = async () => {
    if (taxIdVisible) {
      const uid = Number(selectedUser?.userId || 0);
      if (!uid) return; // new user with no saved value — stay in edit mode

      let freshTaxId = "";
      if (api) {
        try {
          const path = selectedUser?.companyExpense
            ? "companyexpenseaccounts?includeInactive=true"
            : "userinfo?all=true&includeInactive=true";
          const data = await api.get(path);
          const list = Array.isArray(data) ? data : [];
          const fresh = list.find((u: any) => Number(u.userId) === uid);
          freshTaxId = fresh?.taxId ?? "";
        } catch { /* keep current state */ }
      }

      if (!freshTaxId) return; // no saved value on server — stay in edit mode

      setSelectedUser((prev: any) => prev ? { ...prev, taxId: freshTaxId } : prev);
      setTaxIdVisible(false);
      setRevealedTaxId(null);
      return;
    }
    const uid = Number(selectedUser?.userId || 0);
    if (!uid || !api) return;
    if (selectedUser?.companyExpense) return; // expense accounts don't have tax IDs
    try {
      const data = await api.get(`userinfo/${uid}/taxid`);
      const real = typeof data === "string" ? data : (data as any)?.taxId ?? "";
      setRevealedTaxId(real);
      setTaxIdVisible(true);
    } catch {
      setRevealedTaxId(null);
    }
  };

  const [users, setUsers] = useState<UserInfo[]>([]);
  const [companyAccounts, setCompanyAccounts] = useState<UserInfo[]>([]);
  const [states, setStates] = useState<StateInfo[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [assignedClients, setAssignedClients] = useState<any[]>([]);
  const [expandedClients, setExpandedClients] = useState<Set<number>>(new Set());
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [effectiveDateModal, setEffectiveDateModal] = useState({
    show: false,
    changedFields: [] as string[],
    saving: false,
    error: "",
  });
  const [historyView, setHistoryView] = useState<{
    title: string;
    contextLabel: string;
    config: HistoryEndpointConfig;
  } | null>(null);

  useEffect(() => {
    if (selectedUser && !taxIdVisible && !selectedUser.taxId) setTaxIdVisible(true);
  }, [taxIdVisible, selectedUser]);

  const [invites, setInvites] = useState<any[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRoleId, setInviteRoleId] = useState(Roles.User);
  const [inviteNote, setInviteNote] = useState("");
  const [createdToken, setCreatedToken] = useState<any>(null);

  const isAdmin = checkIsAdmin(Number(user?.roleId));
  const isOwnerRole = checkIsOwner(Number(user?.roleId));
  const isCompanyExpenseView = usersFilter === "company";

  const ownerCount = useMemo(() =>
    users.filter((u) => Number(u.roleId) === Roles.Owner).length,
    [users]
  );

  const isSoleOwner =
    selectedUser &&
    Number(selectedUser.roleId) === Roles.Owner &&
    ownerCount <= 1;

  const showStatus= (status: any, message: any) => {
    setUpdateStatus(status);
    setUpdateMessage(message);
  };

  const getStandardUserDisplayName = (u: any) => {
    if (!u) return "";

    const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim();
    if (fullName) return fullName;

    return `User ${u.userId}`;
  };

  const openDayRateHistory = (u: any) => {
    const userId = Number(u?.userId || 0);
    if (!userId) return;
    const displayName = u?.companyExpense
      ? String(u.businessName || `Company expense account ${userId}`)
      : getStandardUserDisplayName(u);
    setHistoryView({
      title: "User Day Rate History",
      contextLabel: `${displayName} - Day Rate`,
      config: {
        readPath: `HistoricalReporting/user-day-rates/${userId}`,
        correctionPath: "HistoricalReporting/user-day-rates",
        rowScopeFields: ["dayRate"],
        valueFields: [{ key: "dayRate", label: "Day Rate", format: formatCurrency }],
      },
    });
  };

  const getCompanyAccountDisplayName = (u: any) => {
    if (!u) return "";

    const accountName = (u.businessName || "").trim();
    if (accountName) return accountName;

    return `Expense Account ${u.userId}`;
  };

  const getInactiveUserDisplayName = (u: any) => {
    if (!u) return "";

    const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim();
    if (fullName) return fullName;

    const accountName = (u.businessName || "").trim();
    if (accountName) return accountName;

    return `User ${u.userId}`;
  };

  const getTenantMembershipDisabled = (u: any) => {
    if (!u) return false;
    if (typeof u.isActive === "boolean") return !u.isActive;
    return false;
  };

  const loadStates = useCallback(async () => {
    if (!api) return [];
    const data = await api.get("stateinfo/");
    const list = Array.isArray(data) ? data : [];
    setStates(list);
    return list;
  }, [api]);

  const loadProjects = useCallback(async (userId: any) => {
    if (!api) return;
    if (!userId) {
      setProjects([]);
      return;
    }

    const data = await api.get(`userinfo/${userId}/assigned-projects`);
    setProjects(Array.isArray(data) ? data : []);
  }, [api]);

  const loadAssignedClients = useCallback(async (userId: any) => {
    if (!api || !userId) {
      setAssignedClients([]);
      return;
    }
    try {
      const data = await api.get(`userinfo/${userId}/assigned-clients`);
      setAssignedClients(Array.isArray(data) ? data : []);
    } catch {
      setAssignedClients([]);
    }
  }, [api]);

  const loadStandardUsers = useCallback(async () => {
    if (!api) return [];
    const data = await api.get("userinfo?all=true&includeInactive=true");
    const list = Array.isArray(data) ? data : [];

    list.sort((a, b) =>
      getStandardUserDisplayName(a)
        .toLowerCase()
        .localeCompare(getStandardUserDisplayName(b).toLowerCase()),
    );

    setUsers(list);
    return list;
  }, [api]);

  const loadCompanyExpenseAccounts = useCallback(async () => {
    if (!api || !hasFeature("company_expense_accounts")) return [];
    const data = await api.get("companyexpenseaccounts?includeInactive=true");
    const list = Array.isArray(data) ? data : [];

    list.sort((a, b) =>
      getCompanyAccountDisplayName(a)
        .toLowerCase()
        .localeCompare(getCompanyAccountDisplayName(b).toLowerCase()),
    );

    setCompanyAccounts(list);
    return list;
  }, [api, hasFeature]);

  const currentList = useMemo(() => {
    if (usersFilter === "company") {
      return [...companyAccounts]
        .filter((u) => !getTenantMembershipDisabled(u))
        .sort((a, b) =>
          getCompanyAccountDisplayName(a)
            .toLowerCase()
            .localeCompare(getCompanyAccountDisplayName(b).toLowerCase()),
        );
    }

    if (usersFilter === "inactive") {
      return [...users]
        .filter((u) => getTenantMembershipDisabled(u))
        .sort((a, b) =>
          getInactiveUserDisplayName(a)
            .toLowerCase()
            .localeCompare(getInactiveUserDisplayName(b).toLowerCase()),
        );
    }

    return [...users]
      .filter((u) => !getTenantMembershipDisabled(u) && !u.companyExpense)
      .sort((a, b) =>
        getStandardUserDisplayName(a)
          .toLowerCase()
          .localeCompare(getStandardUserDisplayName(b).toLowerCase()),
      );
  }, [users, companyAccounts, usersFilter]);

  const toggleClientExpanded = (clientId: number) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const groupedProjects = useMemo(() => {
    const groups: { client: any; projects: any[] }[] = [];

    for (const client of assignedClients) {
      const clientProjects = projects.filter(
        (p: any) => Number(p.clientId) === Number(client.clientId)
      );
      if (clientProjects.length > 0) {
        groups.push({ client, projects: clientProjects });
      }
    }

    const assignedClientIds = new Set(assignedClients.map((c: any) => Number(c.clientId)));
    const ungrouped = projects.filter((p: any) => !assignedClientIds.has(Number(p.clientId)));
    if (ungrouped.length > 0) {
      groups.push({ client: { clientId: 0, businessName: "Other" }, projects: ungrouped });
    }

    groups.sort((a, b) =>
      (a.client.businessName || "").localeCompare(b.client.businessName || "", undefined, { sensitivity: "base" })
    );

    return groups;
  }, [projects, assignedClients]);

  const syncSelection = async (list: any, keepUserId: any) => {
    if (!Array.isArray(list) || list.length === 0) {
      setSelectedUser(null);
      setProjects([]);
      setAssignedClients([]);
      setExpandedClients(new Set());
      return;
    }

    const desired =
      list.find((u) => Number(u.userId) === Number(keepUserId)) ?? list[0];

    setSelectedUser(desired);
    await Promise.all([loadProjects(desired.userId), loadAssignedClients(desired.userId)]);
  };

  const reloadCurrentUsersView = async (keepUserId: any) => {
    const [standard, company] = await Promise.all([
      loadStandardUsers(),
      loadCompanyExpenseAccounts(),
    ]);

    let list = [];

    if (usersFilter === "company") {
      list = [...company]
        .filter((u) => !getTenantMembershipDisabled(u))
        .sort((a, b) =>
          getCompanyAccountDisplayName(a)
            .toLowerCase()
            .localeCompare(getCompanyAccountDisplayName(b).toLowerCase()),
        );
    } else if (usersFilter === "inactive") {
      list = [...standard]
        .filter((u) => getTenantMembershipDisabled(u))
        .sort((a, b) =>
          getInactiveUserDisplayName(a)
            .toLowerCase()
            .localeCompare(getInactiveUserDisplayName(b).toLowerCase()),
        );
    } else {
      list = [...standard]
        .filter((u) => !getTenantMembershipDisabled(u) && !u.companyExpense)
        .sort((a, b) =>
          getStandardUserDisplayName(a)
            .toLowerCase()
            .localeCompare(getStandardUserDisplayName(b).toLowerCase()),
        );
    }

    await syncSelection(list, keepUserId);
  };

  useEffect(() => {
    if (view !== "users") return;

    const currentId = Number(selectedUser?.userId || 0);

    if (
      isCompanyExpenseView &&
      currentId === 0 &&
      selectedUser?.companyExpense
    ) {
      setProjects([]);
      setAssignedClients([]);
      setExpandedClients(new Set());
      return;
    }

    if (!currentList.length) {
      if (isCompanyExpenseView) {
        setSelectedUser(emptyNewCompanyExpenseAccount(states));
      } else {
        setSelectedUser(null);
      }
      setProjects([]);
      setAssignedClients([]);
      setExpandedClients(new Set());
      return;
    }

    const existing =
      currentId > 0
        ? currentList.find((u) => Number(u.userId) === currentId) ?? null
        : null;

    const nextUser = existing ?? currentList[0];

    if (
      !selectedUser ||
      Number(selectedUser.userId) !== Number(nextUser.userId)
    ) {
      setSelectedUser(nextUser);
    }
  }, [currentList, selectedUser, view, isCompanyExpenseView, states]);

  useEffect(() => {
    if (!selectedUser || Number(selectedUser.userId || 0) === 0) {
      setProjects([]);
      setAssignedClients([]);
      setExpandedClients(new Set());
      return;
    }

    loadProjects(selectedUser.userId);
    loadAssignedClients(selectedUser.userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.userId, loadProjects, loadAssignedClients]);

  const handleUserChange = async (e: any) => {
    const raw = e.target.value;
    setTaxIdVisible(false);
    setRevealedTaxId(null);

    if (raw === "0" && isCompanyExpenseView) {
      setSelectedUser(emptyNewCompanyExpenseAccount(states));
      setTaxIdVisible(true);
      setProjects([]);
      setAssignedClients([]);
      setExpandedClients(new Set());
      return;
    }

    const id = Number(raw);
    const u = currentList.find((x) => Number(x.userId) === id) ?? null;
    setSelectedUser(u);
    if (!u?.taxId) setTaxIdVisible(true);

    if (u?.userId) {
      await Promise.all([loadProjects(u.userId), loadAssignedClients(u.userId)]);
    } else {
      setProjects([]);
      setAssignedClients([]);
      setExpandedClients(new Set());
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setSelectedUser((prev: any) => ({
      ...(prev || {}),
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const getSchedulingChangedFields = (u: any) => {
    if (!u || Number(u.userId || 0) === 0) return [];

    const source = isCompanyExpenseView ? companyAccounts : users;
    const original = source.find((x: any) => Number(x.userId) === Number(u.userId));
    if (!original) return [];

    const changed: string[] = [];
    if (Number(original.dayRate || 0) !== Number(u.dayRate || 0)) {
      changed.push("Day Rate");
    }

    if (isCompanyExpenseView) {
      const originalName = String(original.businessName || "").trim();
      const nextName = String(u.businessName || "").trim();
      if (originalName !== nextName) changed.push("Account Name");
    }

    return changed;
  };

  const saveUser = async (u: any, effectiveStartDate?: string | null) => {
    if (!api) throw new Error("API client not ready.");
    if (!u) throw new Error("No user selected.");
    if (tierLoading) throw new Error(EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE);

    const isCreate = Number(u.userId) === 0;

    // Prevent demoting or disabling the sole owner
    if (
      !u.companyExpense &&
      Number(u.userId) > 0 &&
      ownerCount <= 1
    ) {
      const originalUser = users.find((r) => Number(r.userId) === Number(u.userId));
      if (originalUser && Number(originalUser.roleId) === Roles.Owner) {
        if (Number(u.roleId) !== Roles.Owner || u.isActive === false) {
          showStatus("error", "Cannot demote or disable the sole Owner.");
          return undefined;
        }
      }
    }

    if (isCompanyExpenseView) {
      const payload = withEffectiveStartDateIfAllowed({
        businessName: (u.businessName || "").trim() || null,
        firstName: null,
        lastName: null,
        streetAddress: null,
        city: null,
        state: 48,
        zipCode: null,
        phoneNumber: null,
        emailAddress: null,
        taxId: null,
        dayRate: u.dayRate === "" ? 0 : Number(u.dayRate),
        isActive: u.isActive !== false,
      }, isCreate ? todayYmd() : effectiveStartDate, canUseEffectiveHistory);

      const path = isCreate
        ? "companyexpenseaccounts"
        : `companyexpenseaccounts/${u.userId}`;

      if (isCreate) return await api.post<{ userId?: number }>(path, payload);
      await api.put(path, payload);
      return { userId: u.userId };
    }

    const payload = withEffectiveStartDateIfAllowed({
      businessName: (u.businessName || "").trim() || null,
      firstName: (u.firstName || "").trim() || null,
      lastName: (u.lastName || "").trim() || null,
      streetAddress: (u.streetAddress || "").trim() || null,
      city: (u.city || "").trim() || null,
      state: u.state === "" ? 0 : Number(u.state),
      zipCode: (u.zipCode || "").trim() || null,
      phoneNumber: (u.phoneNumber || "").trim() || null,
      emailAddress: (u.emailAddress || "").trim() || null,
      taxId: revealedTaxId !== null ? revealedTaxId : undefined,
      companyExpense: !!u.companyExpense,
      dayRate: u.dayRate === "" ? 0 : Number(u.dayRate),
      roleId: Number(u.roleId || 0),
    }, isCreate ? todayYmd() : effectiveStartDate, canUseEffectiveHistory);

    if (isCreate) return await api.post<{ userId?: number }>("userinfo", payload);
    await api.put(`userinfo/${u.userId}`, payload);
    return { userId: u.userId };
  };

  const performUpdateUser = async (effectiveStartDate?: string | null, fromModal = false) => {
    if (!selectedUser) return;

    try {
      if (fromModal) {
        setEffectiveDateModal((prev) => ({ ...prev, saving: true, error: "" }));
      }
      const isCreate = Number(selectedUser.userId || 0) === 0;
      const saved = await saveUser(selectedUser, effectiveStartDate);
      const keepUserId = Number(saved?.userId || selectedUser.userId || 0);

      // Save manager assignment if approval_workflow is enabled
      let managerFailed = false;
      let managerError = "";
      if (!isCompanyExpenseView && hasFeature("approval_workflow") && keepUserId) {
        try {
          await api!.put(`userinfo/${keepUserId}/manager`, {
            managerUserId: selectedUser.managerUserId
              ? Number(selectedUser.managerUserId)
              : null,
          });
        } catch (managerErr: any) {
          console.error("Failed to save manager assignment:", managerErr);
          managerFailed = true;
          managerError = managerErr?.message || "Manager assignment failed.";
        }
      }

      if (managerFailed) {
        showStatus("error", managerError);
      } else {
        showStatus(
          "success",
          isCreate
            ? "Account created successfully."
            : "User updated successfully.",
        );
      }

      await reloadCurrentUsersView(keepUserId);
      if (fromModal) {
        setEffectiveDateModal({ show: false, changedFields: [], saving: false, error: "" });
      }
    } catch (err: any) {
      console.error(err);
      if (fromModal) {
        setEffectiveDateModal((prev) => ({
          ...prev,
          saving: false,
          error: getEffectiveHistoryErrorMessage(err, err?.message || "Error saving record."),
        }));
      } else {
        showStatus("error", getEffectiveHistoryErrorMessage(err, err?.message || "Error saving record."));
      }
    } finally {
      if (fromModal) {
        setEffectiveDateModal((prev) => (
          prev.show ? { ...prev, saving: false } : prev
        ));
      }
    }
  };

  const updateUser = async (e: any) => {
    e.preventDefault();
    if (!selectedUser) return;
    if (tierLoading) {
      showStatus("error", EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE);
      return;
    }

    const changedFields = getSchedulingChangedFields(selectedUser);
    if (changedFields.length > 0 && canUseEffectiveHistory) {
      setEffectiveDateModal({
        show: true,
        changedFields,
        saving: false,
        error: "",
      });
      return;
    }

    await performUpdateUser(null);
  };

  const toggleUserEnabled= async () => {
    if (!selectedUser || Number(selectedUser.userId || 0) === 0) return;
    if (!api) return;

    // Prevent disabling the sole owner
    if (isSoleOwner && !getTenantMembershipDisabled(selectedUser)) {
      showStatus("error", "Cannot disable the sole Owner.");
      return;
    }

    const currentlyInactive = getTenantMembershipDisabled(selectedUser);

    try {
      if (selectedUser.companyExpense) {
        await api.put(`companyexpenseaccounts/${selectedUser.userId}/status`, {
          isActive: currentlyInactive,
        });
      } else {
        await api.put(`userinfo/${selectedUser.userId}/membership-status`, {
          isActive: currentlyInactive,
        });
      }

      showStatus(
        "success",
        currentlyInactive ? "User re-enabled." : "User disabled.",
      );

      await reloadCurrentUsersView(selectedUser.userId);
    } catch (err: any) {
      console.error(err);
      showStatus(
        "error",
        err?.message ||
          `Error ${currentlyInactive ? "re-enabling" : "disabling"} user.`,
      );
    }
  };

  const loadInvites = useCallback(async () => {
    if (!api) return;
    const data = await api.get("invite");
    setInvites(Array.isArray(data) ? data : []);
  }, [api]);

  const createInvite = async (e: any) => {
    e.preventDefault();
    try {
      if (!api) throw new Error("API client not ready.");
      if (atUserLimit) {
        throw new Error(`${maxUsers} user limit reached.`);
      }
      if (!inviteEmail.trim()) throw new Error("Email is required.");

      const payload = {
        email: inviteEmail.trim(),
        roleId: Number(inviteRoleId || Roles.User),
        note: inviteNote?.trim() || null,
        expiresInDays: 7,
      };

      const result = await api.post("invite", payload);

      const token = (result as any)?.token || null;
      const inviteUrl = token
        ? `${window.location.origin}/invite/accept?token=${encodeURIComponent(token)}`
        : null;

      setCreatedToken(inviteUrl);
      setInviteEmail("");
      setInviteRoleId(2);
      setInviteNote("");

      showStatus("success", "Invite created. Copy the link and send it.");
      await loadInvites();
    } catch (err: any) {
      console.error(err);
      showStatus("error", err?.message || "Error creating invite.");
    }
  };

  const revokeInvite = async (inviteId: any) => {
    try {
      if (!api) throw new Error("API client not ready.");
      await api.post(`invite/revoke/${inviteId}`, {});
      showStatus("success", "Invite revoked.");
      await loadInvites();
    } catch (err: any) {
      console.error(err);
      showStatus("error", err?.message || "Error revoking invite.");
    }
  };

  const copyToClipboard = async (text: any) => {
    try {
      await navigator.clipboard.writeText(text);
      showStatus("success", "Copied to clipboard.");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        showStatus("success", "Copied to clipboard.");
      } catch {
        showStatus("error", "Could not copy to clipboard.");
      }
    }
  };

  useEffect(() => {
    if (!api) return;

    (async () => {
      try {
        await loadStates();
        await loadStandardUsers();
        await loadCompanyExpenseAccounts();
      } catch (e: any) {
        console.error(e);
        showStatus("error", e?.message || "Failed to load data.");
      }
    })();
  }, [api, loadStates, loadStandardUsers, loadCompanyExpenseAccounts]);

  useEffect(() => {
    if (!api) return;
    if (view !== "invites") return;

    (async () => {
      try {
        await loadInvites();
      } catch (e: any) {
        console.error(e);
        showStatus("error", e?.message || "Failed to load invites.");
      }
    })();
  }, [view, api, loadInvites]);

  const TabButton = ({ tabKey, children, disabled, title }: { tabKey: any; children: any; disabled?: any; title?: any }) => {
    const isActive = view === tabKey;

    return (
      <button
        type="button"
        onClick={() => setView(tabKey)}
        disabled={disabled}
        title={title || ""}
        className={`${styles["manage-nav-tab"]}${isActive ? ` ${styles["manage-nav-tab--active"]}` : ""}${
          disabled ? ` ${styles["manage-nav-tab--disabled"]}` : ""
        }`}
      >
        {children}
      </button>
    );
  };

  const ViewToggle = () => {
    const items = [
      { key: "active", label: "Active" },
      { key: "inactive", label: "Inactive" },
      ...(hasFeature("company_expense_accounts")
        ? [{ key: "company", label: "Company Expense Accounts" }]
        : []),
    ];

    return (
      <div className={styles["manage-view-toggle"]} role="tablist" aria-label="User filters">
        {items.map((item) => {
          const active = usersFilter === item.key;

          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setUsersFilter(item.key)}
              className={`${styles["manage-view-toggle__item"]}${active ? ` ${styles["manage-view-toggle__item--active"]}` : ""}`}
              aria-pressed={active}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    );
  };

  const hasSelectedUser = !!selectedUser;
  const selectedUserIsNew = Number(selectedUser?.userId || 0) === 0;
  const selectedUserIsInactive = getTenantMembershipDisabled(selectedUser);
  const showDisableButton = hasSelectedUser && !selectedUserIsNew;
  const companyAccountName = selectedUser?.businessName || "";

  const maxUsers = getLimit("max_users");
  const userCount = users.filter((u: UserInfo) => !u.companyExpense).length;
  const atUserLimit = maxUsers !== null && userCount >= maxUsers;

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={authRequest}
    >
      <>

          <div className={styles["manage-shell"]}>
            <div
              className={styles["manage-nav-tabs"]}
              role="tablist"
              aria-label="Manage users sections"
            >
              <TabButton tabKey="users">Users</TabButton>
              <TabButton
                tabKey="invites"
                disabled={!isAdmin}
                title={!isAdmin ? "Admin only" : ""}
              >
                Invites
              </TabButton>
            </div>

            {view === "users" && (
              <>
                <form onSubmit={updateUser}>
                  <div className={styles["manage-card"]}>
                    <div className={styles["manage-card-header"]}>
                      <h2 className={styles["manage-card-header-title"]}>User Details</h2>
                      {canUseEffectiveHistory && hasSelectedUser && !selectedUserIsNew && (
                        <HistoryIconButton
                          onClick={() => openDayRateHistory(selectedUser)}
                          label={`View effective history for ${
                            isCompanyExpenseView
                              ? companyAccountName || "company expense account"
                              : getStandardUserDisplayName(selectedUser)
                          }`}
                          testId="user-details-history"
                        />
                      )}
                    </div>
                    <div className={styles["manage-card-body"]}>

                   <ViewToggle />

                  {isCompanyExpenseView ? (
                    <div className={styles["manage-subpanel"]}>
                        <h3 className={styles["manage-subpanel__title"]}>
                          Company Expense Account
                        </h3>
                        <p className={styles["manage-subpanel__subtitle"]}>
                          Used for company-billed project expenses that should not be entered under an individual landman.
                        </p>

                        <div className={styles["manage-form-grid"]}>
                          <div className={`${styles["manage-field"]} ${styles["manage-field--half"]}`}>
                            <label className={styles["manage-label"]} htmlFor="expenseAccountSelect">
                              Expense Account
                            </label>
                            <SearchableSelect
                              id="expenseAccountSelect"
                              className={styles["manage-control"]}
                              options={[
                                { value: 0, label: "Create New Company Expense Account" },
                                ...currentList.map((u) => ({ value: Number(u.userId), label: getCompanyAccountDisplayName(u) })),
                              ]}
                              value={selectedUser ? Number(selectedUser.userId) : 0}
                              onChange={(val) => {
                                const fakeEvent = { target: { value: val != null ? String(val) : "0" } };
                                handleUserChange(fakeEvent);
                              }}
                              placeholder="Search expense accounts..."
                            />
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--half"]}`}>
                            <label className={styles["manage-label"]} htmlFor="dayRate">
                              Day Rate
                            </label>
                            <input
                              id="dayRate"
                              className={styles["manage-control"]}
                              type="text"
                              name="dayRate"
                              placeholder="Day Rate"
                              value={selectedUser?.dayRate || ""}
                              onChange={handleInputChange}
                              disabled={!hasSelectedUser}
                            />
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--full"]}`}>
                            <label className={styles["manage-label"]} htmlFor="accountName">
                              Account Name
                            </label>
                            <input
                              id="accountName"
                              className={styles["manage-control"]}
                              type="text"
                              name="businessName"
                              placeholder="Account Name"
                              value={companyAccountName}
                              onChange={handleInputChange}
                              disabled={!hasSelectedUser}
                            />
                          </div>

                          <div className={styles["manage-form-actions"]}>
                            {showDisableButton && (
                              <button
                                type="button"
                                className={styles["manage-btn-danger"]}
                                onClick={toggleUserEnabled}
                                disabled={isSoleOwner && !selectedUserIsInactive}
                              >
                                {selectedUserIsInactive ? "Re-enable" : "Disable"}
                              </button>
                            )}

                            <button
                              type="submit"
                              className={styles["manage-btn"]}
                              disabled={tierLoading || !hasSelectedUser || (selectedUserIsNew && atUserLimit)}
                            >
                              {selectedUserIsNew && atUserLimit
                                ? `${maxUsers} user limit reached`
                                : selectedUserIsNew ? "Create" : "Update"}
                            </button>

                            {isSoleOwner && !selectedUserIsInactive && (
                              <span className={styles["manage-sole-owner-hint"]}>
                                Cannot disable — sole Owner
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                  ) : (
                    <div className={styles["manage-form-grid"]}>
                        <div className={`${styles["manage-field"]} ${styles["manage-field--half"]}`}>
                          <label className={styles["manage-label"]} htmlFor="userSelect">
                            Select User
                          </label>
                            <SearchableSelect
                              id="userSelect"
                              className={styles["manage-control"]}
                              options={currentList.map((u) => ({
                                value: Number(u.userId),
                                label: usersFilter === "inactive"
                                  ? getInactiveUserDisplayName(u)
                                  : getStandardUserDisplayName(u),
                              }))}
                              value={selectedUser ? Number(selectedUser.userId) : null}
                              onChange={(val) => {
                                const fakeEvent = { target: { value: val != null ? String(val) : "" } };
                                handleUserChange(fakeEvent);
                              }}
                              placeholder={!currentList.length ? "No records available" : "Search users..."}
                            />
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--half"]}`}>
                            <label className={styles["manage-label"]} htmlFor="userBusinessName">
                              Business Name
                            </label>
                            <input
                              id="userBusinessName"
                              className={styles["manage-control"]}
                              type="text"
                              name="businessName"
                              placeholder="Business Name"
                              value={selectedUser?.businessName || ""}
                              onChange={handleInputChange}
                              disabled={tierLoading || !hasSelectedUser}
                            />
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--half"]}`}>
                            <label className={styles["manage-label"]} htmlFor="userFirstName">
                              First Name
                            </label>
                            <input
                              id="userFirstName"
                              className={styles["manage-control"]}
                              type="text"
                              name="firstName"
                              placeholder="First Name"
                              value={selectedUser?.firstName || ""}
                              onChange={handleInputChange}
                              disabled={!hasSelectedUser}
                            />
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--half"]}`}>
                            <label className={styles["manage-label"]} htmlFor="userLastName">
                              Last Name
                            </label>
                            <input
                              id="userLastName"
                              className={styles["manage-control"]}
                              type="text"
                              name="lastName"
                              placeholder="Last Name"
                              value={selectedUser?.lastName || ""}
                              onChange={handleInputChange}
                              disabled={!hasSelectedUser}
                            />
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--full"]}`}>
                            <label className={styles["manage-label"]} htmlFor="userStreetAddress">
                              Address
                            </label>
                            <input
                              id="userStreetAddress"
                              className={styles["manage-control"]}
                              type="text"
                              name="streetAddress"
                              placeholder="Address"
                              value={selectedUser?.streetAddress || ""}
                              onChange={handleInputChange}
                              disabled={!hasSelectedUser}
                            />
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--third"]}`}>
                            <label className={styles["manage-label"]} htmlFor="city">
                              City
                            </label>
                            <input
                              id="city"
                              className={styles["manage-control"]}
                              type="text"
                              name="city"
                              placeholder="City"
                              value={selectedUser?.city || ""}
                              onChange={handleInputChange}
                              disabled={!hasSelectedUser}
                            />
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--third"]}`}>
                            <label className={styles["manage-label"]} htmlFor="userState">
                              State
                            </label>
                            <SearchableSelect
                              id="userState"
                              className={styles["manage-control"]}
                              options={states.map((s) => ({ value: s.stateId, label: s.name }))}
                              value={selectedUser?.state ? Number(selectedUser.state) : null}
                              onChange={(val) => setSelectedUser((prev: any) => ({ ...prev, state: val }))}
                              placeholder="Search states..."
                              disabled={!hasSelectedUser}
                            />
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--third"]}`}>
                            <label className={styles["manage-label"]} htmlFor="userZipCode">
                              Zip
                            </label>
                            <input
                              id="userZipCode"
                              className={styles["manage-control"]}
                              type="text"
                              name="zipCode"
                              placeholder="Zip"
                              value={selectedUser?.zipCode || ""}
                              onChange={handleInputChange}
                              disabled={!hasSelectedUser}
                            />
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--half"]}`}>
                            <label className={styles["manage-label"]} htmlFor="userPhoneNumber">
                              Phone Number
                            </label>
                            <input
                              id="userPhoneNumber"
                              className={styles["manage-control"]}
                              type="text"
                              name="phoneNumber"
                              placeholder="Phone Number"
                              value={selectedUser?.phoneNumber || ""}
                              onChange={handleInputChange}
                              disabled={!hasSelectedUser}
                            />
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--half"]}`}>
                            <label className={styles["manage-label"]} htmlFor="emailAddress">
                              Email Address
                            </label>
                            <input
                              id="emailAddress"
                              className={styles["manage-control"]}
                              type="text"
                              name="emailAddress"
                              placeholder="Email Address"
                              value={selectedUser?.emailAddress || ""}
                              onChange={handleInputChange}
                              disabled={!hasSelectedUser}
                            />
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--third"]}`}>
                            <label className={styles["manage-label"]} htmlFor="taxId">
                              Tax ID
                            </label>
                            <div className={styles["manage-control-reveal"]}>
                              <input
                                id="taxId"
                                className={styles["manage-control"]}
                                type="text"
                                name="taxId"
                                placeholder="Tax ID"
                                readOnly={!taxIdVisible}
                                value={taxIdVisible && revealedTaxId !== null ? revealedTaxId : (selectedUser?.taxId || "")}
                                onChange={(e) => {
                                  if (taxIdVisible) setRevealedTaxId(e.target.value);
                                  handleInputChange(e);
                                }}
                                disabled={!hasSelectedUser}
                              />
                              <button
                                type="button"
                                className={styles["manage-reveal-btn"]}
                                onClick={toggleTaxIdVisibility}
                                aria-label={taxIdVisible ? "Hide Tax ID" : "Show Tax ID"}
                                tabIndex={-1}
                                disabled={!hasSelectedUser}
                              >
                                {taxIdVisible ? (
                                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                )}
                              </button>
                            </div>
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--third"]}`}>
                            <label className={styles["manage-label"]} htmlFor="roleLevel">
                              Access Level
                            </label>
                            <select
                              id="roleLevel"
                              className={styles["manage-control"]}
                              name="roleId"
                              value={selectedUser?.roleId ?? ""}
                              onChange={handleInputChange}
                              disabled={!hasSelectedUser || isSoleOwner}
                            >
                              {isOwnerRole && <option value={Roles.Owner}>Owner</option>}
                              <option value={Roles.Admin}>Admin</option>
                              <option value={Roles.ProjectManager}>Project Manager</option>
                              <option value={Roles.User}>User</option>
                            </select>
                            {isSoleOwner && (
                              <span style={{ color: '#dc2626', fontSize: '11px', marginTop: '2px', display: 'block' }}>
                                Cannot change — sole Owner
                              </span>
                            )}
                          </div>

                          <div className={`${styles["manage-field"]} ${styles["manage-field--third"]}`}>
                            <label className={styles["manage-label"]} htmlFor="dayRateStandard">
                              Day Rate
                            </label>
                            <input
                              id="dayRateStandard"
                              className={styles["manage-control"]}
                              type="text"
                              name="dayRate"
                              placeholder="Day Rate"
                              value={selectedUser?.dayRate || ""}
                              onChange={handleInputChange}
                              disabled={!hasSelectedUser}
                            />
                          </div>

                          {hasFeature("approval_workflow") && (
                          <div className={`${styles["manage-field"]} ${styles["manage-field--third"]}`}>
                            <label className={styles["manage-label"]} htmlFor="managerSelect">
                              Manager (Approvals)
                            </label>
                            <SearchableSelect
                              id="managerSelect"
                              className={styles["manage-control"]}
                              options={users
                                .filter((u) => u.isActive !== false && !u.companyExpense && Number(u.userId) !== Number(selectedUser?.userId))
                                .map((u) => ({
                                  value: Number(u.userId),
                                  label: u.firstName && u.lastName
                                    ? `${u.firstName} ${u.lastName}`
                                    : u.businessName || `User ${u.userId}`,
                                }))}
                              value={selectedUser?.managerUserId ? Number(selectedUser.managerUserId) : null}
                              onChange={(val) => setSelectedUser((prev: any) => ({ ...prev, managerUserId: val }))}
                              placeholder="Search managers..."
                              disabled={!hasSelectedUser}
                            />
                          </div>
                          )}

                          <div className={styles["manage-form-actions"]}>
                            {showDisableButton && (
                              <button
                                type="button"
                                className={styles["manage-btn-danger"]}
                                onClick={toggleUserEnabled}
                                disabled={isSoleOwner && !selectedUserIsInactive}
                              >
                                {selectedUserIsInactive ? "Re-enable" : "Disable"}
                              </button>
                            )}

                            <button
                              type="submit"
                              className={styles["manage-btn"]}
                              disabled={!hasSelectedUser}
                            >
                              Update
                            </button>

                            {isSoleOwner && !selectedUserIsInactive && (
                              <span className={styles["manage-sole-owner-hint"]}>
                                Cannot disable — sole Owner
                              </span>
                            )}
                          </div>
                        </div>
                  )}

                    </div>
                  </div>
                </form>

                <div className={styles["manage-card"]}>
                  <div className={styles["manage-card-header"]}>
                    <h2 className={styles["manage-card-header-title"]}>Assigned Prospects</h2>
                  </div>
                  <div className={styles["manage-card-body"]}>
                  {hasSelectedUser && !selectedUserIsNew && projects.length > 0 ? (
                    <div className={styles["manage-prospects-accordion"]}>
                      {groupedProjects.map(({ client, projects: clientProjects }) => {
                        const cid = Number(client.clientId);
                        const isExpanded = expandedClients.has(cid);
                        return (
                          <div key={cid} className={styles["manage-client-group"]}>
                            <button
                              type="button"
                              className={styles["manage-client-group__toggle"]}
                              onClick={() => toggleClientExpanded(cid)}
                              aria-expanded={isExpanded}
                              data-testid={`client-group-${cid}`}
                            >
                              <span className={styles["manage-client-group__arrow"]}>
                                {isExpanded ? "▾" : "▸"}
                              </span>
                              <span className={styles["manage-client-group__name"]}>
                                {client.businessName || "Unknown Client"}
                              </span>
                              <span className={styles["manage-client-group__count"]}>
                                {clientProjects.length}
                              </span>
                            </button>
                            {isExpanded && (
                              <ul className={styles["manage-prospect-list"]}>
                                {clientProjects.map((project: any) => (
                                  <li key={project.projectId} className={styles["manage-prospect-item"]}>
                                    {project.projectName}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={styles["manage-empty"]}>
                      <div className={styles["manage-empty__icon"]}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                          <path d="M3 7l9-4 9 4v6c0 5.25-3.81 9.24-9 10.5C6.81 22.24 3 18.25 3 13V7z" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <p className={styles["manage-empty__title"]}>No assigned prospects</p>
                      <p className={styles["manage-empty__subtitle"]}>This user has no prospect assignments yet.</p>
                    </div>
                  )}
                  </div>
                </div>
              </>
            )}

            {view === "invites" && (
              <>
                <div className={styles["manage-card"]}>
                  <div className={styles["manage-card-header"]}>
                    <h2 className={styles["manage-card-header-title"]}>Create Invite</h2>
                  </div>
                  <div className={styles["manage-card-body"]}>
                  <form onSubmit={createInvite}>
                    <div className={styles["manage-form-grid"]}>
                      <div className={`${styles["manage-field"]} ${styles["manage-field--half"]}`}>
                        <label className={styles["manage-label"]} htmlFor="inviteEmail">
                          Invite Email
                        </label>
                        <input
                          id="inviteEmail"
                          className={styles["manage-control"]}
                          type="text"
                          placeholder="name@example.com"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                        />
                      </div>

                      <div className={`${styles["manage-field"]} ${styles["manage-field--third"]}`}>
                        <label className={styles["manage-label"]} htmlFor="inviteRole">
                          Role
                        </label>
                        <select
                          id="inviteRole"
                          className={styles["manage-control"]}
                          value={inviteRoleId}
                          onChange={(e) => setInviteRoleId(Number(e.target.value))}
                        >
                          {isOwnerRole && <option value={Roles.Owner}>Owner</option>}
                          <option value={Roles.Admin}>Admin</option>
                          <option value={Roles.ProjectManager}>Project Manager</option>
                          <option value={Roles.User}>User</option>
                        </select>
                      </div>

                      <div className={`${styles["manage-field"]} ${styles["manage-field--third"]}`}>
                        <label className={styles["manage-label"]} htmlFor="inviteNote">
                          Note (optional)
                        </label>
                        <input
                          id="inviteNote"
                          className={styles["manage-control"]}
                          type="text"
                          placeholder="Optional note"
                          value={inviteNote}
                          onChange={(e) => setInviteNote(e.target.value)}
                        />
                      </div>

                      <div className={styles["manage-form-actions"]}>
                        <button
                          type="submit"
                          className={styles["manage-btn"]}
                          disabled={atUserLimit}
                        >
                          {atUserLimit ? `${maxUsers} user limit reached` : "Create Invite"}
                        </button>
                      </div>
                    </div>
                  </form>

                  {createdToken && (
                    <div className={styles["manage-invite-url"]}>
                      <div className={styles["manage-invite-url__top"]}>
                        <div>
                          <h4 className={styles["manage-invite-url__title"]}>
                            Invite URL generated
                          </h4>
                          <p className={styles["manage-invite-url__copy"]}>
                            Copy and email this URL to the invitee.
                          </p>
                        </div>

                        <button
                          type="button"
                          className={styles["manage-toolbar-btn"]}
                          onClick={() => copyToClipboard(createdToken)}
                        >
                          <CopyIcon />
                          Copy URL
                        </button>
                      </div>

                      <input
                        className={styles["manage-mono-input"]}
                        readOnly
                        value={createdToken}
                        onFocus={(e) => e.target.select()}
                        aria-label="Invite URL"
                      />
                    </div>
                  )}
                  </div>
                </div>

                <div className={styles["manage-card"]}>
                  <div className={styles["manage-card-header"]}>
                    <h2 className={styles["manage-card-header-title"]}>Existing Invites</h2>
                  </div>
                  <div className={styles["manage-card-body"]}>
                  <div className={styles["manage-invites-toolbar"]}>
                    <div>
                      Refresh to check the latest invite status.
                    </div>

                    <button
                      type="button"
                      className={styles["manage-toolbar-btn"]}
                      onClick={loadInvites}
                    >
                      <RefreshIcon />
                      Refresh
                    </button>
                  </div>

                  {invites.length === 0 ? (
                    <div className={styles["manage-empty"]}>
                      <div className={styles["manage-empty__icon"]}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="5" width="18" height="14" rx="3" stroke="#d1d5db" strokeWidth="1.5" />
                          <path d="M3 9l9 4 9-4" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>
                      <p className={styles["manage-empty__title"]}>No invites</p>
                      <p className={styles["manage-empty__subtitle"]}>No pending or sent invitations.</p>
                    </div>
                  ) : (
                    <div className={styles["manage-invites-list"]}>
                      {invites.map((i) => {
                        const status = (i.status || "Pending").toString();
                        const statusLower = status.toLowerCase();
                        const canRevoke = statusLower === "pending";

                        return (
                          <div className={styles["manage-invite-row"]} key={i.inviteId} data-testid={`invite-row-${i.inviteId}`}>
                            <div className={styles["manage-invite-row__main"]}>
                              <div className={styles["manage-invite-row__top"]}>
                                <div className={styles["manage-invite-row__email"]}>
                                  {i.email}
                                </div>

                                <span
                                  className={`${styles["manage-pill"]} ${
                                    statusLower === "pending"
                                      ? styles["manage-pill--pending"]
                                      : statusLower === "accepted"
                                        ? styles["manage-pill--accepted"]
                                        : styles["manage-pill--revoked"]
                                  }`}
                                >
                                  {status}
                                </span>
                              </div>

                              <div className={styles["manage-invite-row__meta"]}>
                                Role: {RoleLabels[Number(i.roleId)] ?? "Unknown"}
                                {" • "}
                                Expires:{" "}
                                {i.expiresAt
                                  ? new Date(i.expiresAt).toLocaleString()
                                  : "—"}
                                {i.note ? ` • Note: ${i.note}` : ""}
                              </div>
                            </div>

                            <div className={styles["manage-invite-actions"]}>
                              <button
                                type="button"
                                className={styles["manage-invite-action"]}
                                onClick={() => revokeInvite(i.inviteId)}
                                disabled={!canRevoke}
                                title={
                                  canRevoke
                                    ? ""
                                    : "Only pending invites can be revoked"
                                }
                                data-testid={`invite-revoke-${i.inviteId}`}
                              >
                                Revoke
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  </div>
                </div>
              </>
            )}
          </div>
      </>
      {updateStatus && (
        <Toast
          message={updateMessage}
          type={updateStatus as "success" | "error"}
          onClose={() => { setUpdateStatus(null); setUpdateMessage(""); }}
        />
      )}
      <SaveEffectiveDateModal
        show={effectiveDateModal.show}
        changedFieldLabels={effectiveDateModal.changedFields}
        saving={effectiveDateModal.saving}
        errorMessage={effectiveDateModal.error}
        onCancel={() => setEffectiveDateModal({ show: false, changedFields: [], saving: false, error: "" })}
        onConfirm={(effectiveStartDate) => performUpdateUser(effectiveStartDate, true)}
      />
      <HistoricalReportingHistory
        show={!!historyView}
        api={api}
        enabled={canUseEffectiveHistory}
        title={historyView?.title || "Effective History"}
        contextLabel={historyView?.contextLabel || ""}
        config={historyView?.config || null}
        onClose={() => setHistoryView(null)}
        onChanged={() => reloadCurrentUsersView(Number(selectedUser?.userId || 0))}
        onCorrected={(message) => showStatus("success", message)}
      />
    </MsalAuthenticationTemplate>
  );
};

export default ManageUsers;
